/**
 * TKP-89 / tasarım s.21 — modül Ayarlar ekranının saf kuralları. Backend karşılığı: `domain/modules/module-settings/ModuleSettings.ts`.
 * Envanter onay ayarları TKP-101..104 ile devrede; hukuki metin onayı TKP-92 ile. TKP-107: e-posta konuları ve periyodik dönem.
 */
export const SETTING_FIELDS = Object.freeze(["inventoryApprovalEnabled", "inventoryApproverRole", "legalDocumentApprovalRequired", "periodicUpdateMonths", "approvalMode", "inventorySnapshotMonths", "periodicAutoStart"]); // TKP-154: dosyaya kaydedilme süresi; TKP-179: otomatik başlatma
/** TKP-134 (matris D, karar 2026-09-28): onay modu — STANDARD bugünkü tek kademe, PRE_APPROVAL birim → Uyum, NOTIFY onaysız yayın + bildirim. */
export const APPROVAL_MODES = Object.freeze(["STANDARD", "PRE_APPROVAL", "NOTIFY"]);
export const approvalModeLabelKey = (mode) => `module-settings.inventory.approval-mode.${mode}`;
/** TKP-238 a.9: birim modları — "Onaysız Envanter Güncelleme" (NONE) yalnız birim düzeyinde. */
export const DEPARTMENT_APPROVAL_MODES = Object.freeze(["NONE", ...APPROVAL_MODES]);
/** TKP-238 a.23: düzenlenebilir onay e-postaları ve değişkenleri (backend `APPROVAL_MAIL_VARIABLES` ile aynı). */
export const MAIL_TEMPLATE_KEYS = Object.freeze(["submitted", "decided", "periodicStarted", "periodicReminder"]); // TKP-249 e.6: başlatma bilgilendirmesi ve hatırlatma ayrı
export const MAIL_VARIABLES = Object.freeze({
  submitted: ["recipient", "by", "count"],
  decided: ["recipient", "by", "applied", "returned", "edited"],
  periodicStarted: ["recipient", "department", "process", "months"],
  periodicReminder: ["recipient", "department", "days", "count", "departments"],
});
/** TKP-238 a.18: organizasyonun ya da en az bir birimin periyodu varsa "otomatik başlat" anlamlı. */
export const hasPeriods = (form) => Number((form || {}).periodicUpdateMonths) > 0 || Object.values((form || {}).departmentPeriodMonths || {}).some((m) => Number(m) > 0);
/** Birim periyodu kaydı: sayı (0 = "Periyodik Güncelleme Yok", TKP-238 a.14) ya da yok (organizasyon dönemi). */
const ownPeriod = (map, department) => (map && typeof map[department] === "number" && map[department] >= 0 ? map[department] : null);
const trimmed = (t) => ({ subject: String((t && t.subject) || "").trim(), body: String((t && t.body) || "").replace(/\r\n/g, "\n").trim() });
export const EMAIL_TOPICS = Object.freeze(["submitted", "decided"]); // TKP-159: "periodicStarted" koşum başlatma penceresine taşındı
export const PERIODIC_MONTHS_OPTIONS = Object.freeze([0, 3, 6, 12]);
/** TKP-154 / tunesoft2 s.2: envanterin dosyaya otomatik kaydedilme süresi (ay); 0 kapalı. Karar 2026-09-29. */
export const SNAPSHOT_MONTHS_OPTIONS = Object.freeze([0, 1, 3, 6, 12]);
/** TKP-100 (matris B/D "opsiyonel", md.7): onay açıkken taslağa düşmeden doğrudan düzenleyen roller. */
export const DIRECT_EDIT_ROLES = Object.freeze(["DEPARTMENT_MANAGER"]); // TKP-202 n.5: Birim / Süreç Yöneticisi tek rol (eski Süreç Yöneticisi anahtarı 0016 ile birleşti)

export const approverRoleLabelKey = (roleKey) => `module-settings.approver-role.${roleKey}`;

/** Yalnız DEĞİŞEN alanlar gönderilir (backend bilinmeyen alanı reddeder; `product`, `approverRoleOptions` gibi görünüm alanları gitmez). */
export const changedSettings = (original, form) => {
  const patch = {};
  for (const field of SETTING_FIELDS) if ((form || {})[field] !== (original || {})[field]) patch[field] = form[field];
  // e-posta konuları: yalnız değişen konular, kısmi nesne olarak (backend öbür konulara dokunmaz)
  const topics = {};
  for (const topic of EMAIL_TOPICS) {
    const next = !!((form || {}).emailTopics || {})[topic];
    if (next !== !!((original || {}).emailTopics || {})[topic]) topics[topic] = next;
  }
  if (Object.keys(topics).length) patch.emailTopics = topics;
  // TKP-100: onaysız düzenleme rolleri de kısmi nesne
  const roles = {};
  for (const role of DIRECT_EDIT_ROLES) {
    const next = !!((form || {}).directEditRoles || {})[role];
    if (next !== !!((original || {}).directEditRoles || {})[role]) roles[role] = next;
  }
  if (Object.keys(roles).length) patch.directEditRoles = roles;
  // TKP-136: kütüphaneye doğrudan ekleme rolleri (aynı desen)
  const lib = {};
  for (const role of DIRECT_EDIT_ROLES) {
    const next = !!((form || {}).libraryDirectAddRoles || {})[role];
    if (next !== !!((original || {}).libraryDirectAddRoles || {})[role]) lib[role] = next;
  }
  if (Object.keys(lib).length) patch.libraryDirectAddRoles = lib;
  // TKP-134: birim modları — yalnız değişen birimler; kaldırılan birim null ile gider (backend varsayılana döndürür)
  const modes = {};
  const before = ((original || {}).departmentApprovalModes) || {};
  const after = ((form || {}).departmentApprovalModes) || {};
  for (const department of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const next = after[department] || null;
    if (next !== (before[department] || null)) modes[department] = next;
  }
  if (Object.keys(modes).length) patch.departmentApprovalModes = modes;
  // TKP-135: birim periyotları — yalnız değişen birimler; kaldırılan birim null (TKP-238: 0 = birimde periyodik güncelleme yok)
  const months = {};
  const mb = ((original || {}).departmentPeriodMonths) || {};
  const ma = ((form || {}).departmentPeriodMonths) || {};
  for (const department of new Set([...Object.keys(mb), ...Object.keys(ma)])) {
    const next = ownPeriod(ma, department);
    if (next !== ownPeriod(mb, department)) months[department] = next;
  }
  if (Object.keys(months).length) patch.departmentPeriodMonths = months;
  // TKP-238 a.23: e-posta metinleri — yalnız değişen şablon; boş konu + gövde null (varsayılana döner)
  const templates = {};
  for (const key of MAIL_TEMPLATE_KEYS) {
    const next = trimmed(((form || {}).emailTemplates || {})[key]);
    const prev = trimmed(((original || {}).emailTemplates || {})[key]);
    if (next.subject !== prev.subject || next.body !== prev.body) templates[key] = next.subject || next.body ? { ...(next.subject ? { subject: next.subject } : {}), ...(next.body ? { body: next.body } : {}) } : null;
  }
  if (Object.keys(templates).length) patch.emailTemplates = templates;
  return patch;
};

/** Form kopyası: emailTopics iç nesnesi de kopyalanır (v-model doğrudan yazmasın). */
export const formFrom = (settings) => ({ ...settings, emailTopics: { ...((settings && settings.emailTopics) || {}) }, directEditRoles: { ...((settings && settings.directEditRoles) || {}) }, departmentApprovalModes: { ...((settings && settings.departmentApprovalModes) || {}) }, departmentPeriodMonths: { ...((settings && settings.departmentPeriodMonths) || {}) }, libraryDirectAddRoles: { ...((settings && settings.libraryDirectAddRoles) || {}) }, emailTemplates: Object.fromEntries(Object.entries((settings && settings.emailTemplates) || {}).map(([k, t]) => [k, { ...t }])) });

export const hasChanges = (original, form) => Object.keys(changedSettings(original, form)).length > 0;
